import { useNavigate } from 'react-router-dom';
import * as ROUTES from '@/shared/constants/routes';
import { DataTable, StatusField, type Column, type MobileConfig, Pagination, useSpinner } from '@chikara/ui';
import { CardBatch } from '@/features/card/types/card.types';
import styles from './CardBatchesTable.module.css';
import { getStatusType } from '@/features/card/utils/tableStatus';
import { t } from '@/i18n';

interface CardBatchesTableProps {
    data: CardBatch[];
    pagination?: any;
}

export const CardBatchesTable = ({ data, pagination }: CardBatchesTableProps) => {
    const { isSpinnerLoading } = useSpinner();
    const navigate = useNavigate();

    const columns: Column[] = [
        { key: 'requestDate', label: t('dashboard.card.trackingTable.requestDate'), width: '20%' },
        { key: 'requesterName', label: t('dashboard.card.trackingTable.requester'), width: '20%' },
        { key: 'deliveryDate', label: t('dashboard.card.trackingTable.deliveryEst'), width: '15%' },
        { key: 'request_number', label: t('dashboard.card.trackingTable.quantity'), width: '12%', align: 'center' },
        { key: 'type', label: t('dashboard.card.trackingTable.type'), width: '18%', align: 'center' },
        { key: 'status', label: t('dashboard.card.trackingTable.status'), width: '15%', align: 'center' }
    ];

    const mobileConfig: MobileConfig = {
        primaryField: 'requestDate',
        secondaryField: 'requesterName',
        expandableFields: ['deliveryDate', 'request_number', 'type', 'status'],
    };

    return (
        <div className={styles.container}>
            <DataTable
                columns={columns}
                data={data}
                mobileConfig={mobileConfig}
                isLoading={isSpinnerLoading}
                onRowClick={(row) => {
                    if (row.suitcase_uuid) {
                        navigate(`${ROUTES.CARD_TRACKING}/${row.suitcase_uuid}`);
                    }
                }}
                renderCell={(key, value) => {
                    if (key === 'request_number') {
                        return <strong>{value}</strong>;
                    }
                    if (key === 'type') {
                        return <div>{t(`dashboard.card.typeBatch.${value}`)}</div>
                    }
                    if (key === 'status') {
                        return (
                            <StatusField
                                status={t(`dashboard.card.status.${value}`)}
                                statusType={getStatusType(value as string)}
                            />
                        );
                    }
                    return value;
                }}
            />
            {pagination && <Pagination 
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={(page) => pagination.onPageChange(page, pagination.itemsPerPage)}
                showInfo
                infoText={t('dashboard.paginationInfo', {
                    start: (pagination.currentPage - 1) * pagination.itemsPerPage + 1,
                    end: Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems),
                    total: pagination.totalItems
                })}
            />}
        </div>
    );
};
